import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { VendorCredentialIssueRequest } from '@/lib/api-admin/types';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiError as ApiErrorPayload,
  type ApiMeta,
} from '@/lib/http-core';
import { adminFetch } from '@/lib/apiClient';
import type { VendorCredentialIssueResult } from '../types/vendorCredential';
import { vendorApplicationQueryKey } from './useVendorApplicationQuery';

interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

const throwIfError = (raw: unknown, status: number): never => {
  const env = raw as ErrorEnvelopeShape | null | undefined;
  if (env && env.success === false && env.error) {
    throw new ApiClientError(env.error, { status, ...(env.meta ? { meta: env.meta } : {}) });
  }
  throw new TransportError(`Vendor credential issue failed with status ${status}`, raw);
};

export interface VendorCredentialIssueInput {
  applicationId: string;
  body: VendorCredentialIssueRequest;
}

/**
 * G-1(9차): 발급(issue) / 재발급(reset) 공통 POST. 응답에 loginId+oneTimePassword(평문 1회) 포함
 * (be-vendor 계약 §2-1). 재발급(reset-credentials)은 generated OpenAPI 경로에 아직 없을 수 있어
 * adminFetch 로 동적 경로를 호출(카테고리 훅 선례). 응답 새 필드는 VendorCredentialIssueResult.
 */
const useCredentialPostMutation = (action: 'issue-credentials' | 'reset-credentials') => {
  const qc = useQueryClient();
  return useMutation<VendorCredentialIssueResult, Error, VendorCredentialIssueInput>({
    mutationFn: async ({ applicationId, body }) => {
      const path = `/admin/v1/vendor-applications/${applicationId}/${action}`;
      const res = await adminFetch(path, {
        method: 'POST',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('POST', path, body),
        },
        body: JSON.stringify(body),
      });
      const raw = (await res.json().catch(() => null)) as
        | { success?: boolean; data?: VendorCredentialIssueResult }
        | null;
      if (!res.ok || !raw || raw.success !== true || !raw.data) {
        throwIfError(raw, res.status);
      }
      return (raw as { data: VendorCredentialIssueResult }).data;
    },
    onSuccess: async (_data, { applicationId }) => {
      await qc.invalidateQueries({ queryKey: vendorApplicationQueryKey(applicationId) });
      await qc.invalidateQueries({ queryKey: ['admin', 'vendor-applications'] });
    },
  });
};

/** (a) 최초 자격증명 발급 — APPROVED → CREDENTIALS_ISSUED. */
export const useVendorCredentialIssueMutation = () => useCredentialPostMutation('issue-credentials');

/** (c) 비밀번호 재발급 — loginId 유지 + 새 oneTimePassword 1회 노출(분실 대응). */
export const useVendorCredentialResetMutation = () => useCredentialPostMutation('reset-credentials');
